/* eslint-disable react/no-array-index-key */
import type { CFDoc, CFMediaDoc } from '@types';
import { Collapse } from '@components/Collapse';
import Button from '@components/Button';
import Tip from '@components/Tip';
import { Validator } from '@components/Validator';
import arrayMove from '@utils/arrayMove';
import SortableList from '@components/SortableList';
import selectLibDocs from '@modules/mediaLib/utils/selectLibDocs';
import { Dropdown } from '@components/Dropdown';
import ModalSizeSetting from '@modules/panel/components/ModalSizeSetting';
import {
  modalSizeTypeItems,
  defaultModalSize,
  openStyleItems,
} from '@modules/panel/constants';
import CDocItem from './CDocItem';
import { useCustomFeature } from '../../CustomFeatureContext';

const MAX = 10;

function CDocSetting() {
  const { data, setData } = useCustomFeature<CFDoc>();
  const { docs } = data;

  const handleSelectDocs = async () => {
    const { libItems } = await selectLibDocs({ multiple: true });
    if (!libItems) return;
    const items = libItems.slice(0, MAX - docs.length);
    setData((draft) => {
      items.forEach((item) => {
        draft.docs.push({
          mediaId: item.id,
          mediaUrl: item.url,
          mediaName: item.name,
          mediaThumb: item.thumb ?? '',
        });
      });
    });
  };

  const renderItem = (x: CFMediaDoc, i: number) => {
    return (
      <CDocItem
        key={`${i}-${x.mediaId}`}
        data={x}
        index={i}
        listIndex={i}
        onDelete={(index) => {
          setData((draft) => {
            draft.docs.splice(index, 1);
          });
        }}
      />
    );
  };

  return (
    <>
      <Collapse title="PDF设置">
        <Button
          className="w-full"
          disabled={docs.length >= MAX}
          onClick={handleSelectDocs}
        >
          添加PDF
        </Button>
        <div className="mt-20 mb-10 flex">
          <div>PDF列表：</div>
          <div className="ml-10 self-end text-black-gray">{`${docs.length}/${MAX}`}</div>
        </div>
        <Validator
          value={docs}
          validFn={(value) => value.length > 0}
          warningMessage="请添加PDF文件"
        >
          {({ value }) => {
            return value.length ? (
              <SortableList
                axis="y"
                lockAxis="y"
                distance={10}
                onSortEnd={(sort) => {
                  setData((draft) => {
                    draft.docs = arrayMove({
                      array: draft.docs,
                      fromIndex: sort.oldIndex,
                      toIndex: sort.newIndex,
                    });
                  });
                }}
              >
                <div>{value.map(renderItem)}</div>
              </SortableList>
            ) : (
              <>
                <Tip>还未创建PDF文档</Tip>
                <Tip>最多可创建10篇PDF文档</Tip>
              </>
            );
          }}
        </Validator>
      </Collapse>

      <Collapse title="播放控制">
        <div className="mt-20 mb-10">弹出层样式：</div>
        <Dropdown
          items={openStyleItems}
          onChange={(typeId) => {
            setData((draft) => {
              draft.openStyle = typeId;
            });
          }}
          selected={data.openStyle ?? 1}
        />
        <div className="mt-20 mb-10">弹出层尺寸：</div>
        <Dropdown
          items={modalSizeTypeItems}
          onChange={(id) => {
            setData((draft) => {
              draft.modalSizeType = id;
              if (id === 1) {
                draft.size = defaultModalSize;
              }
            });
          }}
          selected={data.modalSizeType ?? 0}
        />
        {data.modalSizeType === 1 && (
          <ModalSizeSetting
            currentModalSize={data.size ?? defaultModalSize}
            onChange={(size) => {
              setData((draft) => {
                draft.size = size;
              });
            }}
          />
        )}
      </Collapse>
    </>
  );
}

export default CDocSetting;
